/**
 * UasZonesControl — the "UAS zones (ŘLP)" box in the mission map's top-right control stack: one
 * toggle per zone theme with its colour, the state reported by UasZonesLayer (loading, layers hidden
 * at this zoom, too many zones, error, no imported data) and the data source with its effective date.
 */
import { useState } from 'react';
import { ChevronDown, ChevronUp, Loader2, ShieldAlert, AlertTriangle } from 'lucide-react';
import {
  UAS_ZONE_THEMES,
  UAS_ZONE_THEME_COLORS,
  formatZoneDate,
  latestEffectiveFrom,
  layerName,
} from '../../lib/uasZones';

const statusMessage = (status, labels, language) => {
  if (!status) return null;
  if (status.catalogueError || status.error) return { tone: 'error', text: labels.error };
  if (status.catalogue && !latestEffectiveFrom(status.catalogue.layers)) return { tone: 'muted', text: labels.noData };
  if (status.tooMany) return { tone: 'warn', text: labels.tooMany };
  if (status.hiddenLayers?.length) {
    const byCode = new Map((status.catalogue?.layers || []).map((layer) => [layer.code, layer]));
    const names = status.hiddenLayers.map((code) => layerName(byCode.get(code), language) || code);
    return { tone: 'muted', text: `${labels.zoomInFor} ${names.join(', ')}` };
  }
  return null;
};

export const UasZonesControl = ({
  enabledThemes,
  onToggleTheme,
  status = null,
  language = 'cs',
  labels = {},
}) => {
  const [expanded, setExpanded] = useState(false);
  const message = statusMessage(status, labels, language);
  const dataDate = formatZoneDate(latestEffectiveFrom(status?.catalogue?.layers), language);
  const warn = status?.tooMany || status?.error || status?.catalogueError;

  return (
    <div
      className="bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border border-gray-200 overflow-hidden"
      style={{ minWidth: expanded ? '220px' : 'auto', maxWidth: '260px' }}
      data-testid="uas-zones-control"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 hover:bg-gray-50 transition-colors"
        data-testid="uas-zones-toggle"
      >
        <span className="flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-orange-600" />
          <span className="text-sm font-medium text-gray-700">{labels.title}</span>
          {status?.loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
          {!status?.loading && warn && <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
        </span>
        {expanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
      </button>

      {expanded && (
        <div className="border-t border-gray-200 p-3 space-y-3">
          <div className="space-y-1">
            {UAS_ZONE_THEMES.map((theme) => {
              const colors = UAS_ZONE_THEME_COLORS[theme];
              return (
                <label key={theme} className="flex items-center gap-2 px-1 py-0.5 rounded text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enabledThemes.includes(theme)}
                    onChange={() => onToggleTheme?.(theme)}
                    data-testid={`uas-zones-theme-${theme}`}
                  />
                  <span
                    className="w-3 h-3 rounded-sm border-2 shrink-0"
                    style={{ borderColor: colors.color, backgroundColor: `${colors.fillColor}40` }}
                  />
                  <span>{labels.themes?.[theme] || theme}</span>
                </label>
              );
            })}
          </div>

          {message && (
            <p
              className={`text-xs ${message.tone === 'error' ? 'text-red-600' : message.tone === 'warn' ? 'text-amber-600' : 'text-slate-500'}`}
              data-testid="uas-zones-status"
            >
              {message.text}
            </p>
          )}

          <div className="border-t border-gray-100 pt-2 text-[11px] text-slate-500 space-y-0.5">
            <p>{labels.source}{dataDate ? ` · ${labels.dataAsOf} ${dataDate}` : ''}</p>
            <p>{labels.disclaimer}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default UasZonesControl;
